import {UseFormReturn} from 'react-hook-form';
import {FormValues} from '../schema';
import {GridFormValues} from '../gridSchema';
import {
  IIngestionSaveDraftPayload,
  IMappingResponse,
} from '../../../../api/types/ingestionApi.type';
import {
  validateMandatoryMappings,
  hasIncompleteNonMandatoryRows,
  buildDraftIngestionMappingPayload,
} from './ingestionMappingUtils';

type SaveDraftContext = {
  form: UseFormReturn<FormValues>;
  gridForm: UseFormReturn<GridFormValues>;
  sourceHeadersData: any;
  lstServerLocation: Array<{value: string; text: string}>;
  ingestionId: number | null;
  setIngestionId: (id: number | null) => void;
  setSaveDraftSuccess: (success: boolean) => void;
  setSaveDraftError: (error: string | null) => void;
  setMappingDraftValidationError: (error: string | null) => void;
  setShowUnmappedConfirmModalForDraft: (show: boolean) => void;
  mappingData: IMappingResponse | null;
  saveDraftMutation: any; // ✅ Add mutation
  isIngestionTypeOverlayAndNoDat: boolean;
};

const buildBasicPayload = (
  values: FormValues,
  sourceHeadersData: any,
  lstServerLocation: Array<{value: string; text: string}>,
  ingestionId: number | null
): IIngestionSaveDraftPayload => {
  return {
    ingestionSpecifySetting: {
      folderLocation: '',
      ingestionId: ingestionId ? String(ingestionId) : '',
      copyOfIngestionID: '',
      rootFolderId: '',
      sourceSystemValue: values.sourceSystem?.key ?? '',
      serverLocationValue: values.serverLocation?.key ?? '',
      folderName:
        values.sourceFolder?.displayValue ?? values.sourceFolder?.key ?? '',
      overwriteValue: values.ingestionType?.displayValue.split(' ')[0] ?? '',
      columnDelimiterValue: values.fieldSeparator?.key ?? '',
      textQulifierValue: values.textQualifier?.key ?? '',
      newLineDelimiterValue: values.multiValue?.key ?? '',
      dateFormatValue: values.dateFormat?.key ?? '',
      timeZoneValue: values.timeZone?.key ?? '',
      isDATFolder: values.sources.dat.enabled,
      loadFilesDATValue: values.sources.dat.loadFile?.key ?? '',
      keyFilesDATID: values.sources.dat.documentKey?.key ?? '',
      keyFilesDATValue: values.sources.dat.documentKey?.displayValue ?? '',
      isNativeFolder: values.sources.native.enabled,
      loadFilesNativeValue: values.sources.native.loadFile?.key ?? '0',
      isDATNative: values.sources.native.isPathInDatFile ?? false,
      filesPathNativeValue:
        values.sources.native.isPathInDatFile &&
        values.sources.native.filePathField?.key
          ? values.sources.native.filePathField.key
          : '0',
      isTextFolder: values.sources.text.enabled,
      loadFilesTextValue: values.sources.text.loadFile?.key ?? '0',
      isDATText: values.sources.text.isPathInDatFile ?? false,
      filesPathTextValue:
        values.sources.text.isPathInDatFile &&
        values.sources.text.filePathField?.key
          ? values.sources.text.filePathField.key
          : '0',
      isPDFFolder: values.sources.pdf.enabled,
      loadFilesPDFValue: values.sources.pdf.loadFile?.key ?? '0',
      isDATPDF: values.sources.pdf.isPathInDatFile ?? false,
      filesPathPDFValue:
        values.sources.pdf.isPathInDatFile &&
        values.sources.pdf.filePathField?.key
          ? values.sources.pdf.filePathField.key
          : '0',
      isTIFFFolder: values.sources.tiff.enabled,
      loadFilesTIFFValue: values.sources.tiff.loadFile?.key ?? '0',
      isDATTIFF: values.sources.tiff.isPathInDatFile ?? false,
      filesPathTIFFValue:
        values.sources.tiff.isPathInDatFile &&
        values.sources.tiff.filePathField?.key
          ? values.sources.tiff.filePathField.key
          : '0',
      isGenerateSearchablePDFforTIFFs:
        values.sources.tiff.generateSearchablePdfs ?? false,
      isMP3VariantFolder: values.sources.mp3.enabled,
      loadFilesMP3VariantValue: values.sources.mp3.loadFile?.key ?? '0',
      isDATMP3Variant: values.sources.mp3.isPathInDatFile ?? false,
      filesPathMP3VariantValue:
        values.sources.mp3.isPathInDatFile &&
        values.sources.mp3.filePathField?.key
          ? values.sources.mp3.filePathField.key
          : '0',
      isTranscriptFolder: values.sources.audio.enabled,
      loadFilesTranscriptValue: values.sources.audio.loadFile?.key ?? '0',
      isDATTranscript: values.sources.audio.isPathInDatFile ?? false,
      filesPathTranscriptValue:
        values.sources.audio.isPathInDatFile &&
        values.sources.audio.filePathField?.key
          ? values.sources.audio.filePathField.key
          : '0',
      isOtherFolder: values.sources.other.enabled,
      linkTypeValue: values.sources.other.linkType?.key ?? '',
      loadFilesOtherValue: values.sources.other.loadFile?.key ?? '0',
      isDATOther: values.sources.other.isPathInDatFile ?? false,
      filesPathOtherValue:
        values.sources.other.isPathInDatFile &&
        values.sources.other.filePathField?.key
          ? values.sources.other.filePathField.key
          : '0',
      sourceColumnHeaders:
        sourceHeadersData?.list?.map((item: any) => ({
          disabled: false,
          group: {disabled: false, name: ''},
          selected: true,
          text: item.text ?? item.value,
          value: String(item.key),
        })) ?? [],
      fileHash: '',
      isDocID: false,
      docIDPrefix: '',
      docIDSuffix: '',
      docIDMinNum: '',
    },
    ingestionMapping: [],
    ingestionMappingFields: {},
    lstServerLocationDetails: lstServerLocation,
  };
};

export const handleSaveDraft = async (context: SaveDraftContext) => {
  const {
    form,
    sourceHeadersData,
    lstServerLocation,
    ingestionId,
    setIngestionId,
    setSaveDraftSuccess,
    setSaveDraftError,
    saveDraftMutation,
  } = context;

  return new Promise<number | null>((resolve, reject) => {
    form.handleSubmit(
      (values) => {
        const payload = buildBasicPayload(
          values,
          sourceHeadersData,
          lstServerLocation,
          ingestionId
        );

        saveDraftMutation.mutate(payload, {
          onSuccess: (data: any) => {
            const newIngestionId = data.ingestionId || ingestionId;
            setIngestionId(newIngestionId);
            setSaveDraftSuccess(true);
            resolve(newIngestionId);
          },
          onError: (error: any) => {
            setSaveDraftError(
              error.message || 'Failed to save draft.  Please try again.'
            );
            reject(error);
          },
        });
      },
      () => {
        reject(new Error('Validation failed'));
      }
    )();
  });
};

// ✅ NEW: Add skipConfirmation parameter
export const handleSaveDraftWithMappings = async (
  context: SaveDraftContext,
  skipConfirmation: boolean = false // ✅ NEW parameter
) => {
  const {
    form,
    gridForm,
    sourceHeadersData,
    lstServerLocation,
    ingestionId,
    setIngestionId,
    setSaveDraftSuccess,
    setSaveDraftError,
    setMappingDraftValidationError,
    setShowUnmappedConfirmModalForDraft,
    mappingData,
    saveDraftMutation,
    isIngestionTypeOverlayAndNoDat,
  } = context;

  return new Promise<number | null>((resolve, reject) => {
    form.handleSubmit(
      (values) => {
        const gridValues = gridForm.getValues();

        // Validate mandatory mappings
        const mandatoryValidation = validateMandatoryMappings(
          gridValues.columnMappings
        );
        if (!mandatoryValidation.ok && !isIngestionTypeOverlayAndNoDat) {
          setMappingDraftValidationError(
            'Select the mappings which is mandatory'
          );
          reject(new Error('Mandatory mappings missing'));
          return;
        }

        // ✅ Check incomplete rows ONLY if not skipping confirmation
        if (
          !skipConfirmation &&
          hasIncompleteNonMandatoryRows(gridValues.columnMappings) &&
          !isIngestionTypeOverlayAndNoDat
        ) {
          setShowUnmappedConfirmModalForDraft(true);
          reject(new Error('Incomplete rows - user confirmation needed'));
          return;
        }

        const payload = buildBasicPayload(
          values,
          sourceHeadersData,
          lstServerLocation,
          ingestionId
        );

        // Add mapping data
        payload.ingestionMapping = isIngestionTypeOverlayAndNoDat
          ? []
          : buildDraftIngestionMappingPayload(gridValues, mappingData);

        saveDraftMutation.mutate(payload, {
          onSuccess: (data: any) => {
            const newIngestionId = data.ingestionId || ingestionId;
            setIngestionId(newIngestionId);
            setSaveDraftSuccess(true);
            resolve(newIngestionId);
          },
          onError: (error: any) => {
            setSaveDraftError(
              error.message || 'Failed to save draft. Please try again.'
            );
            reject(error);
          },
        });
      },
      () => {
        reject(new Error('Validation failed'));
      }
    )();
  });
};
